---
title: Designing for TVs
description: Ya dig?
---

- [Introduction](#introduction)
- [Dealing with scale](#dealing-with-scale)
- [Building realistic prototypes](#building-realistic-prototypes)
  - [Use a gamepad](#use-a-gamepad)
  - [A tip for keyboard prototypes](#a-tip-for-keyboard-prototypes)
  - [Use the Prototyper plugin](#use-the-prototyper-plugin)
  - [Prototopie](#prototopie)
- [Use the correct fidelity](#use-the-correct-fidelity)
- [Don't forget the technical limitations](#dont-forget-the-technical-limitations)
- [Have fun](#have-fun)

## Introduction

These are some tips I've collected over the past few years working with a team who has majority of their users on TV. 

The biggest usablity difference between TVs and other devices is **focus-based navigation** and most workflow issues stem from that. A lot of design tools are based around touch and mouse inputs, but TVs are like life, you know? You have to go through it to get to it. 😅 

The other big difference is the scale. 

## Dealing with scale 

The sizes we use for TV can feel unnatural if you're used to designing for web and mobile. For example, Plex's default font size goes from `16px` on mobile to `24px` on TV. The gaps will also be larger, and the "center" that your eyes go to will be lower than a monitor since you're sitting further back.  

As much as possible, you should be judging your designs on a TV, as far back as you'd normally sit. 

It's important to: 
* Have an easy way to view your work on TV 
* Get back on your couch when you're designing
* Get back on your couch when you're giving feedback

It sounds simple enough, but it's easy to forget. Especially if you go between devices often. 

<img 
  src="/thoughts/designing-for-tvs/original/workspace.jpeg" 
  alt="My workspace, complete with TV and couch"
  className="breakout rounded-lg"
/>

*My workspace, complete with TV and couch*

Doing this will reduce _a lot_ of churn in the design and research phase. 

## Building realistic prototypes

### Use a gamepad

Testing the interface with the wrong input device or paradigm can also lead to misguided feedback. The tensions in the experiences can be too different. What feels awkward or natural with a mouse is not guaranteed to feel awkward or natural with a keyboard. 

If you can, I highly recommend using a gamepad for prototypes. If that's not possible, _at least_ use keyboard controls. I don't recommend using mouse at all. Figma has a really good tutorial on using controllers for input on [Youtube](https://www.youtube.com/watch?v=O_0UIOo5eBA).  

<iframe 
  className="breakout rounded-lg"
  style={{ aspectRatio: '16/9' }}
  src="https://www.youtube.com/embed/O_0UIOo5eBA" 
  title="Figma Tutorial: Using Controllers for Input"
  frameBorder="0" 
  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" 
  allowFullScreen 
/>

*Source: [Youtube](https://www.youtube.com/watch?v=O_0UIOo5eBA)*

### A tip for keyboard prototypes 

Figma has a function to disable the keyboard shortcuts, but the _user_ of the prototype needs to turn it on. If it's not turned on, your prototype might not work due to things like the right arrow moving to the next frame in the document instead of your chosen frame. 

I've found it useful to actually block those keyboard shortcuts inside your prototype, which is possible by adding the other directions and setting a non-existent variable. For example, if you wanted `Escape` to go back instead of `<`, you could do something like this: 

<img 
  src="/thoughts/designing-for-tvs/optimized/blocking-inputs.webp" 
  alt="Blocking keyboard shortcuts in Figma prototype"
  className="breakout rounded-lg"
/>

This way you don't need to worry about your testers needing to turn off shortcuts to avoid getting lost. 

(FYI: a prototype interaction can be copy and pasted the same way as a fill, which can make this process a lot quicker)

### Use the Prototyper plugin 

<img 
  src="/thoughts/designing-for-tvs/optimized/prototyper.webp" 
  alt="Prototyper plugin in Figma"
  className="breakout rounded-lg"
/>

You'll quickly notice that building focused-based prototypes can be a _nightmare_ in Figma. One of the reasons is you'll need to create _a lot_ of frames and screens. Since you can't click on something directly, you'll need a frame for every possible focused item on the way to what you're showing. If your action is on the third row, second column... that's 7 frames to show 1 action. 

[Prototyper](https://www.figma.com/community/plugin/1020894954864594118/prototyper) is an amazing plugin built by Aashrey Sharma, a Senior UX Designer @ Epic Games. 

It allows you to select a component and specificy focus states, and it will automatically build out the frames and prototype interactions required. It's one of the most useful plugins I've ever used. 

<video 
  src="/thoughts/designing-for-tvs/prototyper-example.mp4" 
  className="breakout rounded-lg"
  autoPlay 
  loop 
  muted 
  playsInline
/>

For more on the user experience side of working with focus-based navigation, I highly recommend reading through his article [Press start: How controllers shaped video game design—and where interfaces may go next](https://www.figma.com/blog/press-start-video-game-navigation/) as well as his video tutorial [In the file: Game UI prototyping with the prototyper plugin](https://www.youtube.com/watch?v=6KvnigBr6i4).

### Use Protopie

<video 
  src="/thoughts/designing-for-tvs/prototpie.webm" 
  className="breakout rounded-lg"
  autoPlay 
  loop 
  muted 
  playsInline
/>

*Source: [ProtoPie](https://www.protopie.io)*

So that will solve _some_ issues with Figma, but if you have the opportunity I'd highly recommend trying out Protopie for more advanced prototypes. 

Some of the features you'll get from Protopie are: 
- Native keyboard handling, 
- Variable-driven focus 
- Actual remote control integration, 
- Formulas 
- Extensive variable support

The variables are especially helpful. I like to set an `active_row` or `selected_card` which can make moving focus around a lot easier than in Figma.

Learn more about Protopie's TV here: https://www.protopie.io/solutions/tv

There are a _tonne_ of great tutorials on their website to get started. 

## Use the correct fidelity 

It can take a lot more time and energy to set up a functional TV prototype, so it's more important than ever to make sure you're working at the correct fidelity for what you're trying to learn or communicate. 

Don't make a napkin sketch into a prototype if the napkin sketch would have gotten your point across and the feedback you needed. Save the prototypes for things that matter, and then spend the time needed on them. 

## Don't forget the technical limitations 

Devices can vary wildly. A 2016 Roku Stick has 512MB of RAM compared to the 2024 Roku Ultra's 16GB. 

| Spec | Apple TV 4K (3rd Gen) | Apple TV HD | Roku Ultra (2024) | Roku Streaming Stick 3600 |
|------|----------------------|-------------|-------------------|--------------------------|
| **Processor** | A15 Bionic | A8 (dual-core) | Realtek RTD1319VR | Broadcom BCM2836 |
| **RAM** | 4GB | 2GB | 16GB | 512MB |
| **Storage** | 64GB / 128GB | 32GB | 5GB | 256MB |
| **Resolution** | 4K (2160p) | 1080p | 4K | 1080p |

*Sources: [Apple TV 4K](https://support.apple.com/en-us/111839), [Apple TV HD](https://support.apple.com/en-us/111928), [Roku Ultra 2024](https://www.microcontrollertips.com/teardown-the-new-roku-ultra/), [Roku Stick 3600](https://deviwiki.com/wiki/Roku_Streaming_Stick_(3600R))*

(Roku can be especially gnarly to build for, as there are other limitations like a 4MB package size, and an inability to do gradients with more than 2 colours.)

Thinking of these ahead of time and having a system or documentation built can save a lot of headaches and end up with more robust feeling experiences. 

For example, Plex had a 🥔 `potatoDevice` flag that would turn on and off various effects and features based on the device. When designing something, the team would keep that flag in mind. 

If something meant to be delightful doesn't work right, it doesn't feel delightful at all. So it might as well be simple and fast. 

## Have fun 

That ended up being longer than I expected, but hopefully it was helpful.  Once you get in a groove with some of these things, it can be _really_ fun to build prototypes for TV applications and users and other stakeholders can get really excited for things by using them. 


